import { Button } from "@components/ui/Button"
import { useAuth } from "@features/accounts/useAuth"
import { formatDollars } from "@utils/format-dollars"
import { cn } from "@utils/tw"
import { defineBlock, EditableText, useInlineEditableValue } from "eddev/blocks"
import { AnimatePresence, motion, MotionProps } from "motion/react"
import { useState } from "react"

export const meta: BlockMeta = {
  title: "Membership Levels",
  description: "Displays the available membership levels",
  category: "text",
  icon: "admin-users",
  tags: ["subpage"],
  flags: {
    fullWidth: true,
  },
}

export default defineBlock("memberships/membership-levels", (props) => {
  const auth = useAuth()
  const levels = props.miscSettings?.memberships?.membershipLevels ?? []
  const [listFocused, setListFocused] = useState<null | number>(null)
  const [mode, setMode] = useState<"plus" | "standard">("standard")

  return (
    <div className="w-grid mx-auto my-5">
      <div className="grid-auto lg:w-[80%] gap-y-grid-margin">
        {levels.map((level, i) => {
          let key = "standard"
          let price = formatDollars(level?.priceStandard!)
          if (mode === "plus" && level?.hasPremium) {
            key = "plus"
            price = formatDollars(level?.pricePremium!)
          }

          const tabUnderline = (
            <motion.div
              layoutId={"membership_tabs_" + i}
              className="absolute left-0 right-0 bottom-0 h-[1px] bg-black"
            />
          )

          return (
            <div key={i} className="col-span-12 sm:col-span-6 md:col-span-4 p-4 lg:p-5 bg-grey2 flex flex-col">
              <h3 className="type-label-xl uppercase">
                <span>{level?.label} </span>
                <span className="opacity-50 type-label-xl-light inline-block">
                  <AnimatePresence initial={false} mode="wait">
                    <motion.div key={key} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      {price}
                    </motion.div>
                  </AnimatePresence>
                </span>
              </h3>
              {!!level?.hasPremium && (
                <motion.div layoutRoot className="flex mt-2 gap-2">
                  <button className="type-label-s-light py-1 relative" onClick={() => setMode("standard")}>
                    Standard
                    {mode === "standard" && tabUnderline}
                  </button>
                  <button className="type-label-s-light py-1 relative" onClick={() => setMode("plus")}>
                    Premium
                    {mode === "plus" && tabUnderline}
                  </button>
                </motion.div>
              )}
              <AnimatePresence mode="wait">
                <motion.div
                  key={key}
                  variants={{
                    enter: {},
                    show: { transition: { staggerChildren: 0.05 } },
                    leave: { transition: { duration: 0.1 } },
                  }}
                  initial="enter"
                  animate="show"
                  exit="leave"
                >
                  <motion.div className="type-body-s mt-3" {...fadeVariants}>
                    <EditableText
                      store={`level_description_${level?.label}_${key}`}
                      placeholder="Enter a description for this level"
                    />
                  </motion.div>
                  <div className="type-body-s mt-3 relative">
                    <EditableText
                      as="div"
                      className={cn(listFocused !== i && "absolute opacity-0 inset-0 z-10")}
                      store={`level_items_${level?.label}_${key}`}
                      placeholder="Enter dot points for this level"
                      onFocus={() => setListFocused(i)}
                      onBlur={() => setListFocused(-1)}
                    />
                    {listFocused != i && <TickList id={`level_items_${level?.label}_${key}`} />}
                  </div>
                </motion.div>
              </AnimatePresence>
              <div className="flex-1 flex flex-col justify-end pt-7">
                <Button
                  onClick={() => {
                    auth.openModal("member-signup-form", {
                      eventId: level?.squadupId!,
                    })
                  }}
                >
                  Join or Renew
                </Button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
})

const fadeVariants: MotionProps = {
  variants: {
    enter: { opacity: 0, y: 0 },
    show: { opacity: 1, y: 0, transition: { duration: 0.1, ease: "easeOut" } },
    leave: { opacity: 0, y: 0, transition: { duration: 0.1 } },
  },
  initial: "enter",
  animate: "show",
  exit: "leave",
}

function TickList(props: { id: string }) {
  const [text] = useInlineEditableValue(props.id, "")
  let items = text
    .split(/<br\s?\/?>/g)
    .map((i) => i.trim())
    .filter((i) => !!i)

  if (items.length === 0) {
    if (env.admin) {
      items = ["Click to add dot points"]
    }
  }

  return (
    <motion.ul className="gap-1">
      {items.map((item, i) => (
        <motion.li key={i} className="flex gap-1" {...fadeVariants}>
          <span>{TICK}</span>
          <span>{item}</span>
        </motion.li>
      ))}
    </motion.ul>
  )
}

const TICK = (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15.375 4.93311L14.877 5.43115L7.43555 12.8433L6.9375 13.3413L6.43945 12.8433L2.74805 9.15186L2.25 8.65381L3.2168 7.65771L3.71484 8.15576L6.9375 11.3491L13.8809 4.43506L14.3789 3.93701L15.375 4.93311Z"
      fill="black"
    />
  </svg>
)
